import { config } from '@/config/config';
import { api } from '@/shared/services/baseApiClient';
import { ImageUploadViewModel } from '@/features/webassistant/models';

class ImageUploadService {
  public upload(image: File): Promise<ImageUploadViewModel> {
    return this.validateImage(image).then(() => {
      return this.uploadImage(image);
    });
  }

  private uploadImage(image: File): Promise<ImageUploadViewModel> {
    const url = config().webAssistantHost + '/v1/image';

    const request = new FormData();
    request.append('file', image);

    return api.send<ImageUploadViewModel>('POST', url, request);
  }

  private validateImage(image: File): Promise<void> {
    const maxFilesize = 1000; // 1 MB (in kb)
    const maxWidth = 300;
    const maxHeight = 48;

    return new Promise((resolve: (val?: any) => void, reject) => {
      if (this.bytesToKilobytes(image.size) > maxFilesize) {
        reject({ message: 'Image filesize is too large. Please upload an image under 1MB' });
      }

      this.getDimensions(image)
        .then((dimensions) => {
          if (dimensions.width > maxWidth || dimensions.height > maxHeight) {
            reject({ message: `Image dimensions are too large (${dimensions.width} x ${dimensions.height})` });
          }

          resolve();
        })
        .catch((message) => {
          reject(message);
        });
    });
  }

  private getDimensions(image: File): Promise<ImageDimensions> {
    return this.getImageAsString(image).then((dataUrl) => {
      return this.readFile(dataUrl);
    });
  }

  private readFile(dataUrl: string): Promise<ImageDimensions> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        resolve({
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      };
      image.onerror = () => {
        reject({ message: 'Unsupported file type' });
      };
      image.src = dataUrl;
    });
  }

  private getImageAsString(image: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result as string);
        }
        reject();
      };
    });
  }

  private bytesToKilobytes(bytes: number) {
    return bytes / 1000;
  }
}

interface ImageDimensions {
  width: number;
  height: number;
}

export const imageUploadApi = new ImageUploadService();
